// src/lib/pasteBatch.js — the pure rules behind a paste's review panel (owner 2026-09-17, core 1241).
// The server compares (lib/paste_compare.js) and records; this decides what the form SENDS for the
// comparison, what the panel pre-selects, how related fields move together, and the provenance line.

// What the agent sees right now — typed, unsaved edits included — in the server's field keys.
export function formSnapshotForBatch({ facts = {}, offer = {}, owner = {}, featStates = {}, featAns = {} } = {}) {
  const out = {}
  for (const [k, v] of Object.entries(facts)) if (v !== '' && v != null) out[k] = v
  if (offer.price !== '' && offer.price != null) out.price = offer.price
  if (offer.currency) out.currency = offer.currency
  if (offer.intent) out.intent = offer.intent
  if (owner.name?.trim()) out.owner_name = owner.name.trim()
  if (owner.phone?.trim()) out.owner_phone = owner.phone.trim()
  if (owner.email?.trim()) out.owner_email = owner.email.trim()
  // Lift, parking and heating are drawn from featAns; featStates can hold a stale prefill for them.
  for (const [code, v] of Object.entries(featStates)) {
    if (code === 'elevator' || code === 'parking' || code.startsWith('heat_')) continue
    if (v === 'true' || v === 'false') out[`feature:${code}`] = v
  }
  for (const k of ['elevator', 'parking']) if (featAns[k] === 'true' || featAns[k] === 'false') out[`feature:${k}`] = featAns[k]
  for (const code of featAns.heat || []) out[`feature:${code}`] = 'true'
  return out
}

// Pre-selection: an addition is selected for acceptance; everything else waits for the agent. Rows already
// decided keep their decision. Unchanged rows are not decisions at all.
export function defaultDecisions(fields) {
  const out = {}
  for (const f of fields || []) {
    if (f.decision && f.decision !== 'pending') continue
    if (f.change_kind === 'add') out[f.field] = 'accept'
  }
  return out
}

// Related fields decide together (price+currency, area+unit, kind+subtype): choosing on one row sets the
// others in its group that are still open. Features share a group label but are independent answers.
export function applyDecision(decisions, fields, field, decision) {
  const next = { ...decisions, [field]: decision }
  const row = (fields || []).find(f => f.field === field)
  if (!row || !row.group_key || row.group_key === 'features') return next
  for (const f of fields) {
    if (f.field === field || f.group_key !== row.group_key) continue
    if (f.change_kind === 'unchanged' || (f.decision && f.decision !== 'pending')) continue
    next[f.field] = decision
  }
  return next
}

export function batchCounts(fields) {
  const c = { add: 0, conflict: 0, unchanged: 0, remove: 0, ambiguous: 0 }
  for (const f of fields || []) if (c[f.change_kind] != null) c[f.change_kind]++
  return c
}

// The decisions that can be sent: chosen, for rows that are still open.
export function decisionsToSend(fields, decisions) {
  return (fields || [])
    .filter(f => f.change_kind !== 'unchanged' && (!f.decision || f.decision === 'pending') && decisions[f.field])
    .map(f => ({ field: f.field, decision: decisions[f.field] }))
}

// Provenance per field: the latest ACCEPTED, not undone, decision across all batches.
export function provenanceByField(batches) {
  const out = {}
  for (const b of batches || []) {
    for (const f of b.fields || []) {
      if (f.decision !== 'accept') continue
      const prev = out[f.field]
      if (prev && String(prev.decided_at) >= String(f.decided_at)) continue
      out[f.field] = { value: f.resulting_value, source_kind: f.decided_source_kind || b.source_kind || null,
        decided_by_name: f.decided_by_name || null, decided_at: f.decided_at, proposal_id: b.proposal_id }
    }
  }
  return out
}

// Accepted values → the form's own state shape, so the form shows what was just recorded without a reload.
export function formPatchFromAccepted(fields) {
  const facts = {}, offer = {}, owner = {}, featStates = {}
  for (const f of fields || []) {
    if (f.decision !== 'accept') continue
    const v = f.resulting_value
    // A removal on a non-feature field writes nothing on the server; blanking it here would hide a value still on record.
    if (!f.field.startsWith('feature:') && f.change_kind === 'remove') continue
    if (f.field.startsWith('feature:')) featStates[f.field.slice(8)] = v === false || f.change_kind === 'remove' ? 'false' : 'true'
    else if (['price', 'currency', 'intent'].includes(f.field)) offer[f.field] = v == null ? '' : String(v)
    else if (f.field.startsWith('owner_')) owner[f.field.slice(6)] = v == null ? '' : String(v)
    else facts[f.field] = v == null ? '' : String(v)
  }
  return { facts, offer, owner, featStates }
}
